// Help URL base and supported locales for localization
const HELP_BASE_URL = 'https://kisscal.tattoo/app-doc/Html/'
const SUPPORTED_HELP_LOCALES = ['de', 'en', 'es', 'fr', 'it', 'pl']
const FALLBACK_LOCALE = 'en'

/**
 * Transforms a help URL to the localized version based on artist locale.
 * German (de) URLs remain unchanged (base version), other supported locales get a locale subdirectory.
 * Unsupported locales fall back to English (en).
 * @param {string} baseUrl - The base help URL (German version)
 * @param {string} locale - The artist's locale (e.g., 'de', 'en', 'es', 'fr', 'it')
 * @returns {string} The localized help URL
 */
export const getLocalizedHelpUrl = (baseUrl, locale) => {
    // German (de) or empty locale: return base URL unchanged
    if (!locale || locale === 'de') {
        return baseUrl
    }
    // Determine target locale: use provided if supported, otherwise fallback to English
    const targetLocale = SUPPORTED_HELP_LOCALES.includes(locale) ? locale : FALLBACK_LOCALE
    // German doesn't need transformation (base URL)
    if (targetLocale === 'de') {
        return baseUrl
    }
    // Insert locale directory after Html/
    return baseUrl.replace(HELP_BASE_URL, `${HELP_BASE_URL}${targetLocale}/`)
}

/**
 * Localizes any help URL (which may already contain a locale subdirectory) to the target locale.
 * First normalizes the URL to the German base version, then applies the target locale.
 * @param {string} url - Any help URL (may already have a locale like /en/, /es/, etc.)
 * @param {string} targetLocale - The target locale (e.g., 'de', 'en', 'es', 'fr', 'it')
 * @returns {string} The localized help URL
 */
export const localizeHelpUrl = (url, targetLocale) => {
    if (!url) return url
    // Normalize: remove any existing locale subdirectory to get the German base URL
    const localePattern = new RegExp(`${HELP_BASE_URL}(${SUPPORTED_HELP_LOCALES.join('|')})/`)
    const normalizedUrl = url.replace(localePattern, HELP_BASE_URL)
    // Apply target locale using existing function
    return getLocalizedHelpUrl(normalizedUrl, targetLocale)
}

export const MOBILE_HELP = 'https://kisscal.tattoo/app-doc/Html/00-2-Hilfe-Mobile.html'
export const VOUCHLIST_HELP = 'https://kisscal.tattoo/app-doc/Html/03-1-Gutscheine.html'
export const GENERAL_LIST_HELP = 'https://kisscal.tattoo/app-doc/Html/03-0-Grundlagen-Listen.html'
export const CUSTOMER_DUPLICATE_LIST_HELP = 'https://kisscal.tattoo/app-doc/Html/04-2-Kundendoubletten-bereinigen.html'
export const KISS_SIGN_PAD_SETTING_HELP = 'https://kisscal.tattoo/app-doc/Html/01-7-kissSign.html'
export const ARTIST_SETTINGS_LIST_CONTAINER_HELP = 'https://kisscal.tattoo/app-doc/Html/01-2-Artists-verwalten.html'
export const MESSAGE_TEMPLATE_HELP = 'https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#template-messages'
export const STUDIO_BASE_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#studio-basic-data'
export const STUDIO_PAYMENT_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/02-6-Kundenportal.html#portal-payment-settings'
export const STUDIO_LOCATIONS_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#studio-locations'
export const EDIT_TERMIN_HELP = 'https://kisscal.tattoo/app-doc/Html/02-1-Termine-vereinbaren.html'
export const ICAL_IMPORT_HELP = 'https://kisscal.tattoo/app-doc/Html/01-5-iCal-Import.html'
export const CUSTOMER_IMPORT_HELP = 'https://kisscal.tattoo/app-doc/Html/01-4-Kundenimport.html'
export const ARTIST_MAIL_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/01-2-Artists-verwalten.html#email-data'
export const ARTIST_ISSUE_VOUCHER_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/03-1-Gutscheine.html'
export const ARTIST_INCOME_ASSIST_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/03-3-Einnahmenbuch.html'
export const ARTIST_AUTOCLOSE_ASSIST_SETTINGS_HELP = 'https://kisscal.tattoo/app-doc/Html/02-5-Tagesabschluss.html'
export const ARTIST_CHECK_DEPOSIT_HELP = 'https://kisscal.tattoo/app-doc/Html/03-2-Anzahlungen-managen.html'
export const FORMS_TEMPLATES_HELP = "https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#template-html-forms"
export const CUSTOMER_PORTAL_HELP = "https://kisscal.tattoo/app-doc/Html/02-6-Kundenportal.html"
export const VOUCHER_TEMPLATE_HELP = "https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#template-vouchers"
export const APPOINTMENT_TYPES_HELP = 'https://kisscal.tattoo/app-doc/Html/01-1-Studio-Setup.html#appointment-types'
export const ANALYTICS_HELP = "https://kisscal.tattoo/app-doc/Html/02-7-Analytics.html"
